import { AppBar, Box, Divider, Toolbar, Typography } from '@mui/material'
import { useState, useEffect } from 'react'

import useInnovaTheme, { NAVBAR_HEIGHT } from 'components/common/hooks/useInnovaTheme'
import useOrgIcons from 'components/common/hooks/useOrgIcons'
import useInnovaAuth from 'components/common/hooks/useInnovaAuth'
import useInnovaAxios from 'components/common/hooks/useInnovaAxios'
import { useAppSettings } from 'components/contexts/AppSettingContext'
import { useLocation } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { userPrefsAtom, currentWellDetailsAtom } from 'atoms'
import { useRecoilState, useRecoilValue } from 'recoil'

import InnovaLogoWhite from 'images/innova-white-logo.png'
import NavBarUserProfile from './NavBarUserProfile'
import NavMenu from './NavMenu'
import WellSelector from './WellSelector/WellSelector'
import PageTitleDetails from './PageTitleDetails'

const NavLogo = ({ logoSrc }) => {
  const { displayInnovaLogo } = useOrgIcons()
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box component='img' src={logoSrc} alt='Logo' sx={{ height: (theme) => theme.spacing(6), borderRadius: '5px' }} />
      {displayInnovaLogo() && (
        <>
          <Divider orientation='vertical' flexItem sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 18, letterSpacing: 0.3 }}>Innova</Typography>
        </>
      )}
    </Box>
  )
}

const NavBar = () => {
  const [data, setData] = useState(null)
  const [userPrefs, setUserPrefs] = useRecoilState(userPrefsAtom)
  const currentWellDetails = useRecoilValue(currentWellDetailsAtom)
  const { isAuthenticated } = useAuth0()
  const { user, logout } = useInnovaAuth()
  const { currentOrgLogo } = useOrgIcons()
  const { getNavBarBackground } = useInnovaTheme()
  const location = useLocation()
  const { pageTitle, showActiveWellDetails, wellDetails } = useAppSettings()

  const fetchUserPrefs = useInnovaAxios({
    url: '/user/getPrefs',
  })

  useEffect(() => {
    if (data) {
      setUserPrefs(data)
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated) return
    if (!userPrefs && !data) {
      getUserPrefs()
    }
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAuthenticated) {
      getUserPrefs()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getUserPrefs = async () => {
    const response = await fetchUserPrefs()

    if (response?.data) {
      setData(response.data)
    }
  }

  // special case: peloton landing page is rendered as a standalone 'page'
  if (location?.pathname === '/pelotonlanding') {
    return null
  }

  return (
    <>
      <AppBar
        position='fixed'
        sx={{
          height: NAVBAR_HEIGHT,
          zIndex: (theme) => theme.zIndex.drawer,
          background: getNavBarBackground(),
        }}>
        <Toolbar
          disableGutters
          variant='dense'
          sx={{
            height: NAVBAR_HEIGHT,
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
            alignItems: 'center',
            columnGap: 2,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <NavMenu isAuthenticated={isAuthenticated} />
            <PageTitleDetails
              title={pageTitle}
              wellDetails={wellDetails ?? currentWellDetails}
              showWellDetails={showActiveWellDetails}
            />
          </Box>

          <NavLogo logoSrc={currentOrgLogo || InnovaLogoWhite} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, minWidth: 0 }}>
            {showActiveWellDetails && !wellDetails?.isPlan && <WellSelector />}
            <NavBarUserProfile isAuthenticated={isAuthenticated} logout={logout} />
          </Box>
        </Toolbar>
      </AppBar>
      <Box sx={{ height: NAVBAR_HEIGHT }} />
    </>
  )
}

export default NavBar
